import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { getSchoolIdForUser } from "../../utils/userContext";
import {
    getGradeForClass,
    US_PRIMARY_JUNIOR_GRADE_SCALE,
    MSCE_GRADE_SCALE,
    usesUsGradingScale,
} from "../../utils/gradingUtils";
import { getClassFromSubject, getSubjectId } from "../../utils/teacherSubjects";
import { allowedReportPeriods, defaultReportPeriod, submissionMatchesReportPeriod } from "../../utils/reportPeriodUtils";
import {
    ASSESSMENT_TYPE_OPTIONS,
    LOCKED_MARKS_STATUSES,
    buildStoredExamLabel,
    parseStoredExamLabel,
} from "../../utils/marksAssessmentLabel";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import {
    useClassStudentsQuery,
    useClassMetaQuery,
    useMarksSubmissionsQuery,
} from "../../hooks/teacher/useTeacherOfflineQueries";
import {
    useSaveMarksDraftMutation,
    useSubmitMarksMutation,
} from "../../hooks/teacher/useTeacherOfflineMutations";
import { useOfflineSync } from "../../providers/OfflineSyncProvider";
import OfflineSyncBar from "../../components/offline/OfflineSyncBar";
import useLocalStorageAutosave, {
    AutosaveDraftAlert,
    AutosaveSavedIndicator,
} from "../../hooks/useLocalStorageAutosave";
import {
    applyMarksCsvImport,
    buildMarksCsvTemplate,
    downloadTextFile,
} from "../../utils/marksCsvImport";
import {
    KpiCard,
    ResultsPageContainer,
    ResultsPageHeader,
    ResultsSectionPaper,
    ResponsiveTableWrap,
} from "../admin/examRelated/resultsUi";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../hooks/useTermRecordsScope";

const TERM_OPTIONS = ["Term 1", "Term 2", "Term 3", "Semester 1", "Semester 2"];

function sameTeachingContext(sub, { subjectId, classId, term, academicYear, reportPeriod }) {
    const sameSubject =
        sub.subjectId === subjectId || sub.subject?._id === subjectId || sub.subject?.id === subjectId;
    const sameClass = sub.classId === classId || sub.class?._id === classId || sub.class?.id === classId;
    return (
        sameSubject &&
        sameClass &&
        sub.term === term &&
        String(sub.academicYear) === String(academicYear) &&
        submissionMatchesReportPeriod(sub, reportPeriod)
    );
}

const STATUS_CHIP = {
    Draft: "default",
    Submitted: "info",
    FormApproved: "success",
    Approved: "success",
    Rejected: "error",
};

function gradeChipSx(color) {
    const paletteKey = color === "success" || color === "info" || color === "warning" || color === "error"
        ? color
        : "primary";
    return {
        fontWeight: 700,
        bgcolor: (t) => alpha(t.palette[paletteKey].main, 0.1),
        color: `${paletteKey}.main`,
        borderColor: (t) => alpha(t.palette[paletteKey].main, 0.35),
    };
}

function formatLevelLabel(code) {
    return String(code || "")
        .replace(/^Form(\d)$/i, "Form $1")
        .replace(/^Std(\d)$/i, "Std $1");
}

const TeacherMarksEntry = () => {
    const { currentUser } = useSelector((s) => s.user);
    const teacherId = currentUser?._id || currentUser?.id;
    const schoolId = getSchoolIdForUser(currentUser);
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);
    const {
        subjects,
        activeSubjectId: subjectId,
        activeClassId: classId,
        activeClass,
        subjectName,
        className,
        hasMultipleSubjects,
        setActiveSubjectId,
    } = useTeacherTeaching();
    const { homeroomClasses } = useFormTeacherHomeroom();

    const { isOnline } = useOfflineSync();
    const { data: classMeta = null } = useClassMetaQuery(classId);
    const {
        data: students = [],
        isFetched: studentsFetched,
        error: studentsError,
    } = useClassStudentsQuery(classId);
    const {
        data: allMarksSubs = [],
        isFetched: marksSubsFetched,
        refetch: refetchMarksSubs,
    } = useMarksSubmissionsQuery(teacherId, recordsScope);
    const saveMarksMutation = useSaveMarksDraftMutation(teacherId);
    const submitMarksMutation = useSubmitMarksMutation(teacherId);

    const [marksMap, setMarksMap] = useState({});
    const [submission, setSubmission] = useState(null);

    const loading = !studentsFetched || !marksSubsFetched;

    const isOwnHomeroomClass = useMemo(
        () =>
            Boolean(
                classId &&
                    homeroomClasses.some((c) => String(c._id || c.id) === String(classId)),
            ),
        [classId, homeroomClasses],
    );

    const [saving, setSaving] = useState(false);
    const [showScaleGuide, setShowScaleGuide] = useState(false);

    const [term, setTerm] = useState("Term 1");
    const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
    const [assessmentType, setAssessmentType] = useState("Class test");
    const [assessmentTitle, setAssessmentTitle] = useState("");
    const [reportPeriod, setReportPeriod] = useState("End of term");
    const [submittedHistory, setSubmittedHistory] = useState([]);
    const [viewMode, setViewMode] = useState("new");
    const csvInputRef = useRef(null);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [userHasEdited, setUserHasEdited] = useState(false);

    const assessmentTypeRef = useRef(assessmentType);
    const assessmentTitleRef = useRef(assessmentTitle);
    useEffect(() => {
        assessmentTypeRef.current = assessmentType;
    }, [assessmentType]);
    useEffect(() => {
        assessmentTitleRef.current = assessmentTitle;
    }, [assessmentTitle]);

    const gradingContext = useMemo(
        () => ({
            educationLevel: classMeta?.educationLevel ?? activeClass?.educationLevel,
            levelCode: classMeta?.levelCode ?? activeClass?.levelCode,
            sclassName: classMeta?.sclassName ?? activeClass?.sclassName ?? className,
        }),
        [classMeta, activeClass, className]
    );

    const reportPeriodOptions = useMemo(
        () => allowedReportPeriods(gradingContext.educationLevel),
        [gradingContext.educationLevel]
    );

    useEffect(() => {
        setReportPeriod(defaultReportPeriod(gradingContext.educationLevel));
    }, [gradingContext.educationLevel, classId]);

    const isUsScale = useMemo(
        () =>
            usesUsGradingScale(
                gradingContext.educationLevel,
                gradingContext.levelCode,
                gradingContext.sclassName
            ),
        [gradingContext]
    );

    const storedExamLabel = useMemo(
        () => buildStoredExamLabel(assessmentType, assessmentTitle),
        [assessmentType, assessmentTitle],
    );

    const canEdit = viewMode !== "view" && (!submission || submission.status === "Draft" || submission.status === "Rejected");
    const statusLabel = viewMode === "new" && !submission ? "New" : submission?.status || "New";
    const isLocked = viewMode === "view" || (submission && LOCKED_MARKS_STATUSES.has(submission.status));

    const applyMarksFormFromQueries = useCallback(
        (options = {}) => {
            const { silent = false } = options;
            if (!classId || !teacherId || !studentsFetched || !marksSubsFetched) return;

            if (studentsError && !silent) {
                setMessage(studentsError.message || "Could not load class students");
                setPopupSeverity("error");
                setShowPopup(true);
            }

            const studentList = students;
            const subs = allMarksSubs || [];
            const ctx = { subjectId, classId, term, academicYear, reportPeriod };
            const contextSubs = subs.filter((s) => sameTeachingContext(s, ctx));

            const history = contextSubs
                .filter((s) => LOCKED_MARKS_STATUSES.has(s.status))
                .sort((a, b) => {
                    const ta = new Date(a.submittedAt || a.updatedAt || 0).getTime();
                    const tb = new Date(b.submittedAt || b.updatedAt || 0).getTime();
                    return tb - ta;
                });
            setSubmittedHistory(history);

            const label = buildStoredExamLabel(
                assessmentTypeRef.current,
                assessmentTitleRef.current,
            );
            const editable = contextSubs.find(
                (s) => s.examLabel === label && (s.status === "Draft" || s.status === "Rejected"),
            );

            const applySubmission = (active, mode) => {
                setSubmission(active);
                setViewMode(mode);
                const parsed = parseStoredExamLabel(active.examLabel);
                setAssessmentType(parsed.assessmentType);
                setAssessmentTitle(parsed.assessmentTitle);
                setTerm(active.term || term);
                setAcademicYear(active.academicYear || academicYear);
                const map = {};
                (active.items || []).forEach((item) => {
                    const sid = item.studentId || item.student?._id || item.student?.id;
                    if (sid) map[sid] = String(item.marksObtained ?? "");
                });
                setMarksMap(map);
            };

            if (viewMode === "view" && submission?.id) {
                const viewed = contextSubs.find(
                    (s) => (s.id || s._id) === (submission.id || submission._id),
                );
                if (viewed && LOCKED_MARKS_STATUSES.has(viewed.status)) {
                    applySubmission(viewed, "view");
                } else if (editable) {
                    applySubmission(editable, "edit");
                } else {
                    setSubmission(null);
                    setViewMode("new");
                    const map = {};
                    studentList.forEach((st) => {
                        map[st._id || st.id] = "";
                    });
                    setMarksMap(map);
                }
            } else if (editable) {
                const editableId = editable.id || editable._id;
                const currentId = submission?.id || submission?._id;
                if (
                    String(editableId) !== String(currentId) ||
                    viewMode !== "edit"
                ) {
                    applySubmission(editable, "edit");
                }
            } else {
                setSubmission(null);
                setViewMode("new");
                const map = {};
                studentList.forEach((st) => {
                    map[st._id || st.id] = "";
                });
                setMarksMap(map);
            }
            setUserHasEdited(false);
        },
        [
            classId,
            teacherId,
            subjectId,
            term,
            academicYear,
            reportPeriod,
            viewMode,
            submission?.id,
            submission?._id,
            students,
            allMarksSubs,
            studentsFetched,
            marksSubsFetched,
            studentsError,
        ]
    );

    useEffect(() => {
        applyMarksFormFromQueries();
    }, [applyMarksFormFromQueries]);

    const autosaveStorageKey =
        subjectId && classId
            ? `autosave-marks-${subjectId}-${classId}-${term}-${academicYear}-${reportPeriod}`
            : null;

    const autosaveState = useMemo(
        () => ({ marksMap, term, academicYear, assessmentType, assessmentTitle }),
        [marksMap, term, academicYear, assessmentType, assessmentTitle]
    );

    const {
        draftPrompt,
        restoreDraft,
        discardDraft,
        clearDraft,
        showSavedIndicator,
    } = useLocalStorageAutosave({
        storageKey: autosaveStorageKey,
        state: autosaveState,
        enabled: Boolean(canEdit && !loading && userHasEdited && autosaveStorageKey),
    });

    const handleRestoreDraft = () => {
        const data = restoreDraft();
        if (!data) return;
        if (data.marksMap) setMarksMap(data.marksMap);
        if (data.term) setTerm(data.term);
        if (data.academicYear) setAcademicYear(data.academicYear);
        if (data.assessmentType) setAssessmentType(data.assessmentType);
        if (data.assessmentTitle !== undefined) setAssessmentTitle(data.assessmentTitle);
        setViewMode("edit");
        setUserHasEdited(true);
    };

    const handleStartNewAssessment = () => {
        setViewMode("new");
        setSubmission(null);
        setAssessmentTitle("");
        setUserHasEdited(false);
        const map = {};
        students.forEach((st) => {
            map[st._id || st.id] = "";
        });
        setMarksMap(map);
    };

    const handleViewSubmitted = (sub) => {
        const parsed = parseStoredExamLabel(sub.examLabel);
        setAssessmentType(parsed.assessmentType);
        setAssessmentTitle(parsed.assessmentTitle);
        setSubmission(sub);
        setViewMode("view");
        const map = {};
        (sub.items || []).forEach((item) => {
            const sid = item.studentId || item.student?._id || item.student?.id;
            if (sid) map[sid] = String(item.marksObtained ?? "");
        });
        setMarksMap(map);
    };

    const markedCount = useMemo(
        () =>
            students.filter((st) => {
                const v = marksMap[st._id || st.id];
                return v !== "" && v !== undefined;
            }).length,
        [students, marksMap]
    );

    const buildItems = () =>
        students
            .map((st) => {
                const sid = st._id || st.id;
                const val = marksMap[sid];
                if (val === "" || val === undefined) return null;
                return { studentId: sid, marksObtained: Number(val) };
            })
            .filter(Boolean);

    const handleSaveDraft = async () => {
        const items = buildItems();
        if (!items.length) {
            setMessage("Enter at least one mark before saving.");
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                teacherId,
                schoolId,
                subjectId,
                classId,
                term,
                academicYear,
                examLabel: storedExamLabel,
                reportPeriod,
                items,
                submissionId: submission?.id || submission?._id,
            };
            const { data, queued } = await saveMarksMutation.mutateAsync(payload);
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setPopupSeverity("warning");
            } else {
                setSubmission(data);
                setViewMode("edit");
                clearDraft();
                setUserHasEdited(false);
                setMessage(queued ? "Saved on this device — will upload when online." : "Draft saved");
                setPopupSeverity("success");
            }
            setShowPopup(true);
            await refetchMarksSubs();
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not save draft");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadTemplate = () => {
        if (!students.length) {
            setMessage("No students in this class to build a template.");
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        const safeClass = String(className || "class").replace(/[^\w-]+/g, "_");
        downloadTextFile(`marks-template-${safeClass}.csv`, buildMarksCsvTemplate(students));
    };

    const handleCsvSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file || !canEdit) return;

        try {
            const text = await file.text();
            const result = applyMarksCsvImport({
                csvText: text,
                students,
                existingMarksMap: marksMap,
            });

            if (!result.parsedCount) {
                setMessage(
                    "Could not read any marks from the file. Use columns: rollNum, marks (0–100). Download the template for the correct format.",
                );
                setPopupSeverity("warning");
                setShowPopup(true);
                return;
            }

            setMarksMap(result.marksMap);
            setUserHasEdited(true);

            let msg = `Imported ${result.imported} mark(s) from CSV.`;
            if (result.unmatched.length) {
                msg += ` Roll number(s) not in this class: ${result.unmatched.join(", ")}.`;
            }
            setMessage(msg);
            setPopupSeverity(result.imported ? "success" : "warning");
            setShowPopup(true);
        } catch {
            setMessage("Could not read the CSV file.");
            setPopupSeverity("error");
            setShowPopup(true);
        }
    };

    const handleSubmitFlow = async () => {
        const items = buildItems();
        if (!items.length) {
            setMessage("Enter marks for at least one student.");
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                teacherId,
                schoolId,
                subjectId,
                classId,
                term,
                academicYear,
                examLabel: storedExamLabel,
                reportPeriod,
                items,
                submissionId: submission?.id || submission?._id,
            };
            const subId = submission?.id || submission?._id;
            const { queued } = await submitMarksMutation.mutateAsync({ payload, submissionId: subId });
            clearDraft();
            setUserHasEdited(false);
            setSubmission(null);
            setViewMode("new");
            setAssessmentTitle("");
            const emptyMap = {};
            students.forEach((st) => {
                emptyMap[st._id || st.id] = "";
            });
            setMarksMap(emptyMap);
            setMessage(
                queued
                    ? "Submission queued on this device — will send when online."
                    : "Marks submitted successfully. You can record another assessment below."
            );
            setPopupSeverity("success");
            setShowPopup(true);
            await refetchMarksSubs();
            applyMarksFormFromQueries({ silent: true });
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not submit");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    if (!subjectId || !classId) {
        return (
            <ResultsPageContainer maxWidth="md">
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No class or subject is assigned yet. Ask your administrator to assign you a subject, or pick one from
                    the sidebar after it appears on your profile.
                </Alert>
                <Button component={RouterLink} to="/Teacher/dashboard" sx={{ mt: 2 }}>
                    Back to dashboard
                </Button>
            </ResultsPageContainer>
        );
    }

    const levelLabel = formatLevelLabel(gradingContext.levelCode) || className;
    const scaleLabel = isUsScale
        ? "US grading (Primary & Junior Secondary)"
        : "MSCE grading (Form 3–4)";

    return (
        <Box
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <ResultsPageContainer>
                <ResultsPageHeader
                    eyebrow="Results & marks"
                    title="Submit subject results"
                    subtitle={
                        isOwnHomeroomClass
                            ? `${className} · ${subjectName}. You are the form teacher for this class — your marks go straight to Class results to send to the director.`
                            : `${className} · ${subjectName}. Enter marks, save a draft, then submit to this class's form teacher. After director approval, results appear on the student portal.`
                    }
                />

                <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
                <TermRecordsScopeAlert
                    scope={recordsScope}
                    currentTermLabel={currentTermLabel}
                    sectionLabel="marks submissions"
                />

                <OfflineSyncBar />

                {!isOnline && (
                    <Chip
                        size="small"
                        label="Offline — marks sync when online"
                        color="warning"
                        variant="outlined"
                        sx={{ mb: 2 }}
                    />
                )}

                {hasMultipleSubjects ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <MenuBookOutlinedIcon color="primary" fontSize="small" />
                            <Typography variant="subtitle2" fontWeight={700}>
                                Subject & class
                            </Typography>
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {subjects.map((sub) => {
                                const id = getSubjectId(sub);
                                const cls = getClassFromSubject(sub);
                                const selected = String(id) === String(subjectId);
                                return (
                                    <Chip
                                        key={id}
                                        label={`${sub.subName || "Subject"} — ${cls?.sclassName || "Class"}`}
                                        clickable
                                        color={selected ? "primary" : "default"}
                                        variant={selected ? "filled" : "outlined"}
                                        onClick={() => setActiveSubjectId(id)}
                                    />
                                );
                            })}
                        </Stack>
                    </Paper>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                        <KpiCard title="Students" value={students.length} loading={loading} color="primary" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <KpiCard
                            title="Marks entered"
                            value={loading ? "—" : `${markedCount} / ${students.length}`}
                            loading={loading}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <KpiCard title="Grading scale" value={isUsScale ? "US" : "MSCE"} loading={loading} color="secondary" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <KpiCard
                            title="Status"
                            value={statusLabel}
                            loading={loading}
                            color={submission?.status === "Approved" ? "success" : "warning"}
                        />
                    </Grid>
                </Grid>

                {isLocked ? (
                    <Alert
                        severity={submission?.status === "Approved" ? "success" : "info"}
                        icon={<LockOutlinedIcon />}
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        <strong>{statusLabel}</strong>
                        {" — "}
                        <strong>{storedExamLabel}</strong>
                        {submission?.status === "Submitted"
                            ? isOwnHomeroomClass
                                ? " · Ready in Class results — send to the director when you are done."
                                : " · Sent to your form teacher for review. Use Record new assessment to enter another test."
                            : submission?.status === "FormApproved"
                              ? isOwnHomeroomClass
                                  ? " · Ready to send to the director from Class results."
                                  : " · Accepted by form teacher, pending director."
                              : submission?.status === "Approved"
                                ? " · Published on student records."
                                : " · Read-only."}
                        {submission?.rejectionNote ? (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Note: {submission.rejectionNote}
                            </Typography>
                        ) : null}
                        <Button size="small" sx={{ mt: 1 }} onClick={handleStartNewAssessment}>
                            Record new assessment
                        </Button>
                    </Alert>
                ) : submission?.status === "Rejected" ? (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        <strong>Rejected</strong>
                        {submission.rejectionNote ? ` — ${submission.rejectionNote}` : " — Update marks and submit again."}
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        Each assessment (e.g. Test 1, Test 2, continuous assessment) is saved separately. After you submit, it moves to{" "}
                        <strong>Submitted</strong> below and you can record a new one with a different assessment name.
                        Grading uses <strong>{scaleLabel}</strong> for {levelLabel}.
                    </Alert>
                )}

                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small" disabled={!canEdit || loading}>
                                <InputLabel>Term</InputLabel>
                                <Select
                                    label="Term"
                                    value={term}
                                    onChange={(e) => {
                                        setUserHasEdited(true);
                                        setTerm(e.target.value);
                                    }}
                                >
                                    {TERM_OPTIONS.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Academic year"
                                value={academicYear}
                                disabled={!canEdit || loading}
                                onChange={(e) => {
                                    setUserHasEdited(true);
                                    setAcademicYear(e.target.value);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small" disabled={!canEdit || loading}>
                                <InputLabel>Report period</InputLabel>
                                <Select
                                    label="Report period"
                                    value={reportPeriod}
                                    onChange={(e) => {
                                        setUserHasEdited(true);
                                        setReportPeriod(e.target.value);
                                    }}
                                >
                                    {reportPeriodOptions.map((l) => (
                                        <MenuItem key={l} value={l}>
                                            {l}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small" disabled={!canEdit || loading}>
                                <InputLabel>Assessment type</InputLabel>
                                <Select
                                    label="Assessment type"
                                    value={assessmentType}
                                    onChange={(e) => {
                                        const nextType = e.target.value;
                                        setUserHasEdited(true);
                                        setViewMode("new");
                                        setAssessmentType(nextType);
                                        assessmentTypeRef.current = nextType;
                                        window.setTimeout(() => applyMarksFormFromQueries({ silent: true }), 0);
                                    }}
                                >
                                    {ASSESSMENT_TYPE_OPTIONS.map((l) => (
                                        <MenuItem key={l} value={l}>
                                            {l}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Assessment name"
                                placeholder="e.g. Test 1, Test 2, March CA"
                                value={assessmentTitle}
                                disabled={!canEdit || loading}
                                helperText={
                                    storedExamLabel
                                        ? `Saved as: ${storedExamLabel}`
                                        : "Use a unique name for each test"
                                }
                                onChange={(e) => {
                                    setUserHasEdited(true);
                                    setAssessmentTitle(e.target.value);
                                }}
                                onBlur={() => applyMarksFormFromQueries({ silent: true })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip
                                    label={statusLabel}
                                    color={STATUS_CHIP[statusLabel] || "default"}
                                    sx={{ fontWeight: 700 }}
                                />
                                <Chip size="small" variant="outlined" label={scaleLabel} />
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                {isUsScale ? (
                    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
                        <Button
                            fullWidth
                            onClick={() => setShowScaleGuide((v) => !v)}
                            startIcon={<InfoOutlinedIcon />}
                            sx={{
                                justifyContent: "flex-start",
                                py: 1.25,
                                px: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                color: "text.primary",
                            }}
                        >
                            US grade scale (Primary & Form 1–2)
                        </Button>
                        <Collapse in={showScaleGuide}>
                            <ResponsiveTableWrap>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Marks %</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>US grade</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {US_PRIMARY_JUNIOR_GRADE_SCALE.map((row) => (
                                            <TableRow key={row.range}>
                                                <TableCell>{row.range}</TableCell>
                                                <TableCell>{row.label}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={row.letter} sx={{ fontWeight: 800 }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ResponsiveTableWrap>
                        </Collapse>
                    </Paper>
                ) : (
                    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
                        <Button
                            fullWidth
                            onClick={() => setShowScaleGuide((v) => !v)}
                            startIcon={<InfoOutlinedIcon />}
                            sx={{
                                justifyContent: "flex-start",
                                py: 1.25,
                                px: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                color: "text.primary",
                            }}
                        >
                            MSCE grade scale (Form 3–4)
                        </Button>
                        <Collapse in={showScaleGuide}>
                            <ResponsiveTableWrap>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Marks</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {MSCE_GRADE_SCALE.map((row) => (
                                            <TableRow key={row.grade}>
                                                <TableCell>{row.range}</TableCell>
                                                <TableCell>{row.label}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={row.grade} sx={{ fontWeight: 800 }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ResponsiveTableWrap>
                        </Collapse>
                    </Paper>
                )}

                {submittedHistory.length > 0 ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={1}
                            sx={{ mb: 1.5 }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <HistoryOutlinedIcon color="action" fontSize="small" />
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Submitted ({submittedHistory.length})
                                </Typography>
                            </Stack>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<AddOutlinedIcon />}
                                onClick={handleStartNewAssessment}
                            >
                                Record new assessment
                            </Button>
                        </Stack>
                        <Stack spacing={0.75}>
                            {submittedHistory.map((sub) => {
                                const id = sub.id || sub._id;
                                const itemCount = Array.isArray(sub.items) ? sub.items.length : 0;
                                const when = sub.submittedAt || sub.updatedAt;
                                const whenLabel = when
                                    ? new Date(when).toLocaleDateString(undefined, {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                      })
                                    : "";
                                const viewing =
                                    viewMode === "view" &&
                                    (submission?.id === id || submission?._id === id);
                                return (
                                    <Stack
                                        key={id}
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        flexWrap="wrap"
                                        gap={1}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 1,
                                            bgcolor: (t) =>
                                                viewing
                                                    ? alpha(t.palette.primary.main, 0.08)
                                                    : alpha(t.palette.grey[500], 0.06),
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {sub.examLabel || "Assessment"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {itemCount} students · {sub.status}
                                                {whenLabel ? ` · ${whenLabel}` : ""}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant={viewing ? "contained" : "outlined"}
                                            onClick={() => handleViewSubmitted(sub)}
                                        >
                                            View
                                        </Button>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Paper>
                ) : null}

                <AutosaveDraftAlert
                    draft={draftPrompt}
                    onRestore={handleRestoreDraft}
                    onDiscard={discardDraft}
                />

                <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={handleCsvSelected}
                />

                <Box sx={{ position: "relative" }}>
                <ResultsSectionPaper
                    title="Student marks"
                    action={
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            {saving ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CircularProgress size={18} />
                                    <Typography variant="caption" color="text.secondary">
                                        Saving…
                                    </Typography>
                                </Stack>
                            ) : null}
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadOutlinedIcon />}
                                disabled={loading || !students.length}
                                onClick={handleDownloadTemplate}
                            >
                                CSV template
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<UploadFileOutlinedIcon />}
                                disabled={!canEdit || loading || !students.length}
                                onClick={() => csvInputRef.current?.click()}
                            >
                                Upload CSV
                            </Button>
                        </Stack>
                    }
                >
                    <AutosaveSavedIndicator show={showSavedIndicator} />
                    <ResponsiveTableWrap>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell width={72}>Roll</StyledTableCell>
                                    <StyledTableCell>Student</StyledTableCell>
                                    <StyledTableCell width={120}>Marks %</StyledTableCell>
                                    <StyledTableCell width={100}>
                                        {isUsScale ? "US grade" : "Grade"}
                                    </StyledTableCell>
                                    <StyledTableCell>Description</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {loading
                                    ? Array.from({ length: 5 }).map((_, i) => (
                                          <StyledTableRow key={i}>
                                              <StyledTableCell colSpan={5}>
                                                  <Skeleton height={36} />
                                              </StyledTableCell>
                                          </StyledTableRow>
                                      ))
                                    : null}
                                {!loading && students.length === 0 ? (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                            <Typography color="text.secondary">
                                                No students in {className}. Ask your administrator to enrol learners.
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ) : null}
                                {!loading &&
                                    students.map((st) => {
                                        const sid = st._id || st.id;
                                        const val = marksMap[sid] ?? "";
                                        const grade =
                                            val !== ""
                                                ? getGradeForClass(val, {
                                                      ...gradingContext,
                                                      ...activeClass,
                                                  })
                                                : null;
                                        return (
                                            <StyledTableRow
                                                key={sid}
                                                sx={{
                                                    bgcolor: (t) =>
                                                        grade && !grade.passed
                                                            ? alpha(t.palette.error.main, 0.04)
                                                            : undefined,
                                                }}
                                            >
                                                <StyledTableCell>{st.rollNum}</StyledTableCell>
                                                <StyledTableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {st.name}
                                                    </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        fullWidth
                                                        placeholder="0–100"
                                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                                        value={val}
                                                        disabled={!canEdit || loading}
                                                        onChange={(e) => {
                                                            setUserHasEdited(true);
                                                            const raw = e.target.value;
                                                            if (raw === "") {
                                                                setMarksMap((m) => ({ ...m, [sid]: "" }));
                                                                return;
                                                            }
                                                            const n = Math.min(100, Math.max(0, Number(raw)));
                                                            setMarksMap((m) => ({
                                                                ...m,
                                                                [sid]: Number.isFinite(n) ? String(n) : "",
                                                            }));
                                                        }}
                                                        sx={{ maxWidth: 100 }}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {grade ? (
                                                        <Chip
                                                            size="small"
                                                            label={grade.grade}
                                                            variant="outlined"
                                                            sx={gradeChipSx(grade.color)}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.disabled">
                                                            —
                                                        </Typography>
                                                    )}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {grade ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {grade.label}
                                                            {grade.passed ? "" : " · Not passed"}
                                                        </Typography>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </ResponsiveTableWrap>
                </ResultsSectionPaper>
                </Box>

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    justifyContent="flex-end"
                    sx={{ mt: 1 }}
                >
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<SaveOutlinedIcon />}
                        disabled={!canEdit || saving || loading}
                        onClick={handleSaveDraft}
                    >
                        Save draft
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={isLocked ? <LockOutlinedIcon /> : <SendOutlinedIcon />}
                        disabled={!canEdit || saving || loading}
                        onClick={handleSubmitFlow}
                    >
                        {isOwnHomeroomClass ? "Submit to class results" : "Submit to form teacher"}
                    </Button>
                </Stack>
            </ResultsPageContainer>

            <Popup
                message={message}
                showPopup={showPopup}
                setShowPopup={setShowPopup}
                severity={popupSeverity}
            />
        </Box>
    );
};

export default TeacherMarksEntry;
